import {useContext, useEffect, useState} from "react";
import {Link, Navigate, useNavigate, useParams} from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ImageWithLoader from "../../components/image-loader/image-with-loader.component";
import {
  createBlogPost,
  getBlogPostBySlug,
  updateBlogPost,
} from "../../utils/firebase/firebase.utils";
import {isAdmin} from "../../utils/blog.constants";
import {UserContext} from "../../context/user.context";
import "./blog-post.styles.scss";
import "./blog-editor.styles.scss";

const EMPTY_FORM = {
  title: "",
  excerpt: "",
  coverImage: "",
  tags: "",
  content: "",
};

const BlogEditor = () => {
  const {slug} = useParams();
  const isEditing = Boolean(slug);
  const navigate = useNavigate();
  const {currentUser} = useContext(UserContext);
  const admin = isAdmin(currentUser);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEditing) return;
    const fetchPost = async () => {
      try {
        const post = await getBlogPostBySlug(slug);
        if (post) {
          setEditingId(post.id);
          setForm({
            title: post.title || "",
            excerpt: post.excerpt || "",
            coverImage: post.coverImage || "",
            tags: (post.tags || []).join(", "),
            content: post.content || "",
          });
        } else {
          setError("Post not found.");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load the post.");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, isEditing]);

  // Only admins may reach the editor.
  if (currentUser !== null && !admin) {
    return <Navigate to="/blog" replace/>;
  }

  const handleChange = (event) => {
    const {name, value} = event.target;
    setForm((prev) => ({...prev, [name]: value}));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError("Title and content are required.");
      return;
    }
    setSaving(true);
    setError("");

    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      coverImage: form.coverImage.trim(),
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      content: form.content,
    };

    try {
      if (isEditing) {
        await updateBlogPost(editingId, payload);
        navigate(`/blog/${slug}`);
      } else {
        const newSlug = await createBlogPost(payload, currentUser);
        navigate(`/blog/${newSlug}`);
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong while saving. Please try again.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="blog-editor">
        <p className="editor-status">Loading…</p>
      </div>
    );
  }

  return (
    <div className="blog-editor">
      <div className="editor-container">
        <div className="editor-topbar">
          <Link to="/blog" className="blog-back-link">
            ← All posts
          </Link>
          <h1 className="editor-heading">
            {isEditing ? "Edit post" : "New post"}
          </h1>
        </div>

        {error && <p className="editor-error">{error}</p>}

        <form className="editor-form" onSubmit={handleSubmit}>
          <div className="editor-fields">
            <label className="editor-label">
              Title
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="An interesting title"
                required
              />
            </label>

            <label className="editor-label">
              <span className="label-text">Excerpt <span className="hint">(shown on cards)</span></span>
              <textarea
                name="excerpt"
                value={form.excerpt}
                onChange={handleChange}
                placeholder="A one or two line summary…"
                rows={2}
              />
            </label>

            <div className="editor-row">
              <label className="editor-label">
                Cover image URL
                <input
                  type="text"
                  name="coverImage"
                  value={form.coverImage}
                  onChange={handleChange}
                  placeholder="https://… or /blog-covers/…"
                />
              </label>

              <label className="editor-label">
                <span className="label-text">Tags <span className="hint">(comma separated)</span></span>
                <input
                  type="text"
                  name="tags"
                  value={form.tags}
                  onChange={handleChange}
                  placeholder="react, career, notes"
                />
              </label>
            </div>
          </div>

          <div className="editor-split">
            <label className="editor-label editor-content-label">
              <span className="label-text">Content <span className="hint">(Markdown supported)</span></span>
              <textarea
                name="content"
                value={form.content}
                onChange={handleChange}
                placeholder="Write your post in Markdown…"
                className="editor-content-area"
              />
            </label>

            <div className="editor-preview">
              <span className="preview-label">Live preview</span>
              <div className="markdown-body preview-body">
                {form.content ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      img: ({node, ...props}) => (
                        <ImageWithLoader {...props} />
                      ),
                    }}
                  >
                    {form.content}
                  </ReactMarkdown>
                ) : (
                  <p className="preview-placeholder">
                    Your formatted post will appear here.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="editor-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving
                ? "Saving…"
                : isEditing
                  ? "Update post"
                  : "Publish post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogEditor;
